// InsertForm.js
import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import DataTable_AgGrid from './DataTable_AgGrid';
import './Forms.css';
import { useAuth } from './AuthContext';
import api from './axios';

export default function InsertForm({ insertJsonDef, onSubmit, onCancel }) {
  const navigate = useNavigate();
  const [formDef, setFormDef] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [loadingDropdowns, setLoadingDropdowns] = useState({});

  const [error, setError] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [showPopupMessage, setShowPopupMessage] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Bepaal of ergens nog geladen wordt
  const anyLoading = Object.values(loadingDropdowns).some(v => v);

  const { user, companyId, logout } = useAuth();


  // 1. Initialiseer formDef & lege formValues
  useEffect(() => {
    if (!insertJsonDef) return;
    const parsed = typeof insertJsonDef === 'string'
      ? JSON.parse(insertJsonDef)
      : insertJsonDef;
    setFormDef(parsed);

    const initial = {};
    (parsed.layout || []).forEach(item => {
      const fields =
        item.type === 'widget'         ? item.fields || [] :
        item.type === 'tabpage-layout' ? item.layoutConfig.column1 || [] :
        item.type === 'subtable'       ? item.subtableConfig.columns || [] :
        [];
      // alleen niet-hidden velden initialiseren
      fields
        .filter(fld => !fld.hide)
        .forEach(fld => {
          initial[fld.field] = '';
        });
    });
    setFormValues(initial);
  }, [insertJsonDef]);

  // 2. Éénmalig laden van alle dropdowns zonder dependingfield
  useEffect(() => {
    if (!formDef) return;

    (async () => {
      const email = user.email;

      // Loop over alle dropdown-velden uit je layout
      const dropdowns = formDef.layout
        .flatMap(item => item.fields || [])
        .filter(f =>
          f.type === 'dropdown' &&
          f.listquery &&
          !f.dependingfield &&
          !f.hide
        );

      for (const fld of dropdowns) {
        // Markeer loading voor dit veld
        setLoadingDropdowns(prev => ({ ...prev, [fld.field]: true }));

        try {
          const payload = {
            companyId,
            email,
            query: fld.listquery,
            params: fld.params === 'user_email' ? [email] : []
          };

          const { data } = await api.post('/api/iAGateway_get_query', payload);

          // Sla resultaten of lege array op
          setDropdownOptions(prev => ({
            ...prev,
            [fld.field]: Array.isArray(data) ? data : []
          }));
        } catch (err) {
          console.error(`Error loading dropdown ${fld.field}:`, err);
          setDropdownOptions(prev => ({ ...prev, [fld.field]: [] }));
        } finally {
          // Zet loading uit voor dit veld
          setLoadingDropdowns(prev => ({ ...prev, [fld.field]: false }));
        }
      }
    })();
  }, [formDef, user.email, companyId, setDropdownOptions, setLoadingDropdowns]);

  useEffect(() => {
    if (!formDef) return;

    // Vind alle dropdowns die afhankelijk zijn van een ander veld
    const dependentFields = formDef.layout
      .flatMap(item => item.fields || [])
      .filter(f =>
        f.type === 'dropdown' &&
        f.listquery &&
        f.dependingfield &&
        !f.hide
      );

    // Haal voor elke afhankelijk veld de parent-waarde
    const parentValues = dependentFields
      .map(f => formValues[f.dependingfield]);

    // Skip als nog geen parent-waarden bekend zijn
    if (!parentValues.some(v => v)) return;

    (async () => {
      const email = user.email;

      for (const fld of dependentFields) {
        const parentVal = formValues[fld.dependingfield];

        // Als parent leeg → leeg maken en verder
        if (!parentVal) {
          setDropdownOptions(prev => ({ ...prev, [fld.field]: [] }));
          continue;
        }

        // Zet loading aan
        setLoadingDropdowns(prev => ({ ...prev, [fld.field]: true }));

        // Zoek in de bestaande opties de record die overeenkomt met parentVal
        const parentOpts = dropdownOptions[fld.dependingfield] || [];
        const recordKey = fld.dependingparameterrecordfield || Object.keys(parentOpts[0] || {})[0];
        const parentRec = parentOpts.find(o => String(o[recordKey]) === String(parentVal));

        if (!parentRec) {
          setDropdownOptions(prev => ({ ...prev, [fld.field]: [] }));
          setLoadingDropdowns(prev => ({ ...prev, [fld.field]: false }));
          continue;
        }

        // Parameter voor de backend
        const paramValue = parentRec[recordKey];

        try {
          const payload = {
            companyId,
            email,
            query: fld.listquery,
            params: [paramValue]
          };

          const { data } = await api.post('/api/iAGateway_get_query', payload);

          setDropdownOptions(prev => ({
            ...prev,
            [fld.field]: Array.isArray(data) ? data : []
          }));
        } catch (err) {
          console.error(`Error loading dependent dropdown ${fld.field}:`, err);
          setDropdownOptions(prev => ({ ...prev, [fld.field]: [] }));
        } finally {
          setLoadingDropdowns(prev => ({ ...prev, [fld.field]: false }));
        }
      }
    })();
  },
  // Depend on formDef plus *elke* parent-waarde dynamisch:
  [
    formDef,
    ...(
      formDef
        ? formDef.layout
            .flatMap(item => item.fields || [])
            .filter(f => f.type === 'dropdown' && f.dependingfield)
            .map(f => formValues[f.dependingfield])
        : []
    )
  ]);

  const handleChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // 1) Verzamel alle velden uit de layout
    const allFields = formDef.layout
      .flatMap(item =>
        item.type === 'widget'            ? item.fields :
        item.type === 'tabpage-layout'    ? item.layoutConfig.column1 :
        item.type === 'subtable'          ? item.subtableConfig.columns :
        []
      );
  
    // 2) Verplichte velden check (skip fixedvalue)
    const mandatoryFields = allFields
      .filter(f => f.type !== 'fixedvalue' && f.mandatory !== false)
      .map(f => f.field);
  
    const missing = mandatoryFields.filter(name => {
      const v = formValues[name];
      return v == null || String(v).trim() === '';
    });
  
    if (missing.length) {
      const missingLabels = allFields
        .filter(f => missing.includes(f.field))
        .map(f => f.label);

      setError(`Fill in all required fields: ${missingLabels.join(', ')}`);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);

      return;
    }
  
    // 3) Haal kolomnamen uit insert_query
    const colsPart = formDef.insert_query.match(/\(([^)]+)\)/)[1];
    const columns  = colsPart.split(',').map(s => s.trim());

    // 4) Bouw de uiteindelijke SQL-string door %s te vervangen
    let completeSql = formDef.insert_query;
    for (const col of columns) {
      const fld = allFields.find(f => f.field === col);
      let replacement;

      if (fld.type === 'fixedvalue') {
        // vaste waarde óf functie
        replacement = fld.valuefunction
          ? fld.valuefunction
          : `'${fld.value}'`;
      } else {
        const val = formValues[col];
        // lege waarde?
        if (val === '' || val == null) {
          // voor tekst/memo‐velden lege string, anders NULL
          if (fld.type === 'text' || fld.type === 'rtf_memo' || fld.type === 'plaintext_memo') {
            replacement = `''`;
          } else {
            replacement = 'NULL';
          }
        } else {
          // niet‐leeg: strings ontsnappen, anders as‐is
          if (typeof val === 'string') {
            replacement = `'${val.replace(/'/g, "''")}'`;
          } else {
            replacement = val;
          }
        }
      }

      completeSql = completeSql.replace('%s', replacement);
    }
  
    //console.log('Volledige query:', completeSql);
  
    // hier kun je completeSql naar jouw backend sturen...
    setIsLoading(true);
    try {    
      const payload = {
        companyId,
        email:        user.email,
        insert_query: completeSql
      }
    
      setIsLoading(true)
    
      // axios-instance (api) voegt Authorization-header en cookies automatisch toe
      await api.post('/api/iAGateway_insert_query', payload)
    
      setMessage('Successfully added record')
      setShowPopupMessage(true)
      setTimeout(() => {
        setShowPopupMessage(false)
        onSubmit && onSubmit()
      }, 3000)
    
    } catch (err) {
      setError('Error adding new record: ' + (err.response?.data?.error || err.message))
      setShowErrorPopup(true)
      setTimeout(() => setShowErrorPopup(false), 3000)
    } finally {
      setIsLoading(false)
    }
  };  
  
  
  const handleCancel = () => onCancel ? onCancel() : navigate(-1);

  if (!formDef) {
    return <div className="loader-wrapper-new"><div className="loader-new" /></div>;
  }

  const widgetItems   = formDef.layout.filter(i => i.type === 'widget');
  const tabItems      = formDef.layout.filter(i => ['tabpage-layout','subtable'].includes(i.type));
  const topWidgets    = widgetItems.slice(0, 2);
  const bottomWidgets = widgetItems.slice(2);

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '20px'
  };

  // Render input based on field type
  const renderInput = fld => {
    const val = formValues[fld.field] || '';
    switch (fld.type) {
      case 'text':
        return (
          <textarea
            rows={1}
            value={val}
            onChange={e => handleChange(fld.field, e.target.value)}
            onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
            style={{
              width: '100%',
              height: '32px',
              lineHeight: '32px',
              padding: '0 8px',
              boxSizing: 'border-box',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              resize: 'none',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={val}
            onChange={e => handleChange(fld.field, e.target.value)}
            style={{ width: '100%', padding: '8px' }}
          />
        );
        case 'dropdown': {
          // RAW ophalen en defensief omzetten naar array
          const raw = dropdownOptions[fld.field];
        
          const opts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        
          return (
            <select
              value={formValues[fld.field] || ''}
              required={fld.mandatory !== false}
              onChange={e => handleChange(fld.field, e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="" disabled>Select...</option>
              {opts.map((opt, i) => {
                const valKey = opt.hasOwnProperty(fld.field)
                  ? fld.field
                  : (fld.valuefield || Object.keys(opt)[0]);
        
                let label;
                if (fld.valueGetter) {
                  try {
                    label = new Function('data', `return ${fld.valueGetter}`)(opt);
                  } catch {
                    label = fld.list_showcolumn
                      ? opt[fld.list_showcolumn]
                      : String(opt[valKey]);
                  }
                } else {
                  label = fld.list_showcolumn
                    ? opt[fld.list_showcolumn]
                    : String(opt[valKey]);
                }
        
                return (
                  <option key={i} value={opt[valKey]}>
                    {label}
                  </option>
                );
              })}
            </select>
          );
        }
                           
      case 'rtf_memo':
        return (
          <textarea
            value={val}
            onChange={e => handleChange(fld.field, e.target.value)}
            style={{ width: '100%', padding: '8px', height: fld.height || '150px', resize: 'none' }}
          />
        );
      default:
        return null;
    }
  };

  // Render each field in the grid
  const renderFieldGrid = fld => {
    if (fld.type === 'rtf_memo') {
      return (
        <div style={{ gridColumn: '1 / -1', marginBottom: '10px' }}>
          <div className="header_widget_1_key_text" style={{ marginBottom: '5px' }}>
            {fld.headerName}
          </div>
          <div className="header_widget_1_value_text">
            {renderInput(fld)}
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="header_widget_1_key_text" style={{ display: 'flex', alignItems: 'center' }}>
          {fld.label}
        </div>
        <div className="header_widget_1_value_text" style={{ display: 'flex', alignItems: 'center' }}>
          {renderInput(fld)}
        </div>
      </>
    );
  };

  // Bepaal per veld welk label er bij het laden getoond moet worden
  const loadingFields = Object.entries(loadingDropdowns)
    .filter(([_, v]) => v)
    .map(([field]) => {
      const fldDef = formDef
        .layout
        .flatMap(item => item.fields || [])
        .find(f => f.field === field);
      return fldDef?.label || field;
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}>

      {/* Header + buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ color: 'gray' }}>
          <h2 className="form_header_text" style={{ color: '#333', margin: 0 }}>
            {formDef.formtitle}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleCancel} className="create-button">Cancel</button>
          <button onClick={handleSave} className="create-button">Save</button>
        </div>
      </div>

      {/* Top widgets */}
      {topWidgets.length > 0 && (
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-between', alignItems: 'start' }}>
          {topWidgets.map((w, i) => (
            <div
              key={i}
              style={{ ...cardStyle, flex: 1, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '7px', alignContent: 'start', height: w.height, maxheight: w.maxheight, width: w.width, maxwidth: w.maxwidth }}
            >
              {w.fields
                .filter(fld => !fld.hide)  
                .map((fld, fi) => (
                  <Fragment key={fi}>{renderFieldGrid(fld)}</Fragment>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Bottom widgets */}
      {bottomWidgets.map((w, i) => (
        <div
          key={i}
          style={{ ...cardStyle, marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '7px', alignContent: 'start', height: w.height, maxheight: w.maxheight, width: w.width, maxwidth: w.maxwidth }}
        >
          {w.fields
            .filter(fld => !fld.hide)
            .map((fld, fi) => (
              <Fragment key={fi}>{renderFieldGrid(fld)}</Fragment>
          ))}
        </div>
      ))}

      {/* Onderste tab-widget */}
      {tabItems.length > 0 && (
        <div style={{ ...cardStyle, marginTop: '20px' }}>
          <Tabs>
            <TabList className="tabList" style={{ borderBottom: '1px solid #ccc', width: '100%' }}>
              {tabItems.map((tab, idx) => (
                <Tab key={idx} className="tabButton" selectedClassName="tabButtonActive">
                  {tab.label || `Tab ${idx + 1}`}
                </Tab>
              ))}
            </TabList>
            {tabItems.map((tab, idx) => (
              <TabPanel
                key={idx}
                className="tabPanel"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  height: tab.layoutConfig.column1?.[0]?.height || '52vh',
                  maxHeight: tab.layoutConfig.column1?.[0]?.maxheight || '52vh',
                  paddingTop: '10px',
                  gap: '20px'
                }}
              >
                {tab.type === 'tabpage-layout' && tab.layoutConfig.column1 && (
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '7px', alignContent: 'start' }}>
                    {tab.layoutConfig.column1.map((fld, fi) => <Fragment key={fi}>{renderFieldGrid({ ...fld, label: fld.headerName })}</Fragment>)}
                  </div>
                )}
                {tab.type === 'subtable' && tab.subtableConfig && (
                  <div style={{ flex: 1 }}>
                    <DataTable_AgGrid
                      initialData={[]}
                      columns={tab.subtableConfig.columns}
                      isEditable
                      config={tab.subtableConfig}
                    />
                  </div>
                )}
              </TabPanel>
            ))}
          </Tabs>
        </div>
      )}

      {showPopupMessage && <div className="popup-message">{message}</div>}
      {showErrorPopup && <div className="popup">{error}</div>}

      { (anyLoading || isLoading) && (
        <div
          className="loader-wrapper-new"
          style={{
            zIndex: 9999
          }}
        >
          <div className="loader-new"></div>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#666',
              fontSize: '1.1rem',
            }}
          >
            {isLoading
              ? 'Saving…'
              : `Loading ${loadingFields.join(', ') || '…'}...`
            }
          </div>
        </div>
      ) }


    </div>
  );
}
